'use client';

import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Send, CheckCircle2, Loader } from 'lucide-react';
import { EASE_OUT, EASE_CUSTOM } from '@/lib/animations'

const inputClass =
    'w-full px-4 py-2.5 rounded-xl border border-slate-200/60 bg-white/70 text-slate-900 placeholder-slate-400 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/40 focus:border-blue-300 transition-all backdrop-blur-sm';

interface QuickLeadModalProps {
    open: boolean;
    onClose: () => void;
    context?: string;
}

export default function QuickLeadModal({ open, onClose, context = '' }: QuickLeadModalProps) {
    const [form, setForm] = useState({ nombre: '', email: '', asunto: context, info: '' });
    const [status, setStatus] = useState<'idle' | 'sending' | 'success' | 'error'>('idle');

    // Lock body scroll when modal is open
    useEffect(() => {
        if (open) document.body.style.overflow = 'hidden';
        else document.body.style.overflow = '';
        return () => { document.body.style.overflow = ''; };
    }, [open]);

    // Reset form when opened
    useEffect(() => {
        if (open) {
            setForm({ nombre: '', email: '', asunto: context, info: '' });
            setStatus('idle');
        }
    }, [open, context]);

    const set = (k: string, v: string) => setForm(prev => ({ ...prev, [k]: v }));

    const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        setStatus('sending');
        try {
            const res = await fetch('https://formspree.io/f/xdawnkdn', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
                body: JSON.stringify({
                    _subject: `Lead directo — ${form.asunto || context}`,
                    nombre: form.nombre,
                    email: form.email,
                    asunto: form.asunto,
                    informacion: form.info,
                }),
            });
            setStatus(res.ok ? 'success' : 'error');
        } catch {
            setStatus('error');
        }
    };

    return (
        <AnimatePresence>
            {open && (
                <>
                    {/* Backdrop */}
                    <motion.div
                        key="backdrop"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.2 }}
                        className="fixed inset-0 z-[80] bg-slate-900/40 backdrop-blur-sm"
                        onClick={onClose}
                    />

                    {/* Modal */}
                    <motion.div
                        key="modal"
                        initial={{ opacity: 0, scale: 0.95, y: 24 }}
                        animate={{ opacity: 1, scale: 1, y: 0 }}
                        exit={{ opacity: 0, scale: 0.95, y: 16 }}
                        transition={{ duration: 0.3, ease: EASE_OUT }}
                        className="fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-[90] w-full max-w-md px-4"
                    >
                        <div className="rounded-2xl border border-white/60 bg-white/90 backdrop-blur-2xl shadow-[0_24px_80px_rgba(15,23,42,0.18)] p-6 md:p-8">
                            {/* Header */}
                            <div className="flex items-start justify-between mb-5">
                                <div>
                                    <h2 className="text-lg font-black text-slate-900 tracking-tight">Cuéntanos qué necesitas</h2>
                                    <p className="text-xs text-slate-500 mt-0.5">Un agente te contactará en menos de unas horas.</p>
                                </div>
                                <button
                                    onClick={onClose}
                                    className="w-8 h-8 rounded-xl border border-slate-200/60 bg-slate-50/80 flex items-center justify-center text-slate-400 hover:text-slate-700 hover:bg-slate-100 transition-all shrink-0 ml-4"
                                >
                                    <X className="w-4 h-4" />
                                </button>
                            </div>

                            <AnimatePresence mode="wait">
                                {status === 'success' ? (
                                    <motion.div
                                        key="success"
                                        initial={{ opacity: 0, scale: 0.96 }}
                                        animate={{ opacity: 1, scale: 1 }}
                                        className="flex flex-col items-center gap-4 py-8 text-center"
                                    >
                                        <div className="w-14 h-14 rounded-2xl bg-green-50 border border-green-100 flex items-center justify-center">
                                            <CheckCircle2 className="w-7 h-7 text-green-600" />
                                        </div>
                                        <div>
                                            <p className="font-bold text-slate-900 text-base">¡Recibido!</p>
                                            <p className="text-slate-500 text-sm mt-1 max-w-[260px] mx-auto leading-snug">
                                                En menos de unas horas uno de nuestros agentes se pondrá en contacto contigo.
                                            </p>
                                        </div>
                                        <button
                                            onClick={onClose}
                                            className="text-sm text-blue-600 font-medium hover:underline underline-offset-2"
                                        >
                                            Cerrar
                                        </button>
                                    </motion.div>
                                ) : (
                                    <motion.form
                                        key="form"
                                        onSubmit={handleSubmit}
                                        initial={{ opacity: 1 }}
                                        exit={{ opacity: 0 }}
                                        className="flex flex-col gap-3.5"
                                    >
                                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                            <div>
                                                <label className="block text-[11px] font-semibold text-slate-500 mb-1.5 uppercase tracking-wide">Nombre</label>
                                                <input
                                                    type="text"
                                                    placeholder="Tu nombre"
                                                    value={form.nombre}
                                                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => set('nombre', e.target.value)}
                                                    disabled={status === 'sending'}
                                                    className={inputClass}
                                                />
                                            </div>
                                            <div>
                                                <label className="block text-[11px] font-semibold text-slate-500 mb-1.5 uppercase tracking-wide">
                                                    Email <span className="text-blue-500">*</span>
                                                </label>
                                                <input
                                                    type="email"
                                                    placeholder="tu@empresa.com"
                                                    value={form.email}
                                                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => set('email', e.target.value)}
                                                    required
                                                    disabled={status === 'sending'}
                                                    className={inputClass}
                                                />
                                            </div>
                                        </div>

                                        <div>
                                            <label className="block text-[11px] font-semibold text-slate-500 mb-1.5 uppercase tracking-wide">Asunto</label>
                                            <input
                                                type="text"
                                                placeholder="¿Qué necesitas?"
                                                value={form.asunto}
                                                onChange={(e: React.ChangeEvent<HTMLInputElement>) => set('asunto', e.target.value)}
                                                disabled={status === 'sending'}
                                                className={inputClass}
                                            />
                                        </div>

                                        <div>
                                            <label className="block text-[11px] font-semibold text-slate-500 mb-1.5 uppercase tracking-wide">Información adicional</label>
                                            <textarea
                                                placeholder="Cuéntanos tu negocio, qué problema quieres resolver, presupuesto aproximado..."
                                                value={form.info}
                                                onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => set('info', e.target.value)}
                                                rows={3}
                                                disabled={status === 'sending'}
                                                className={`${inputClass} resize-none`}
                                            />
                                        </div>

                                        {status === 'error' && (
                                            <p className="text-xs text-red-500 bg-red-50 border border-red-100 rounded-xl px-3 py-2">
                                                Error al enviar. Escríbenos a{' '}
                                                <a href="mailto:info@innovatheratech.com" className="underline">info@innovatheratech.com</a>
                                            </p>
                                        )}

                                        <button
                                            type="submit"
                                            disabled={status === 'sending' || !form.email}
                                            className="mt-1 w-full py-3 rounded-xl bg-blue-600 text-white font-semibold text-sm flex items-center justify-center gap-2 shadow-[0_4px_16px_rgba(37,99,235,0.3)] hover:bg-blue-700 transition-all active:scale-[0.99] disabled:opacity-50 disabled:cursor-not-allowed"
                                        >
                                            {status === 'sending'
                                                ? <><Loader className="w-4 h-4 animate-spin" /> Enviando...</>
                                                : <><Send className="w-4 h-4" /> Enviar y esperar respuesta</>
                                            }
                                        </button>

                                        <p className="text-[10px] text-slate-400 text-center">
                                            Sin spam. Datos confidenciales. Un agente real te responde en horas.
                                        </p>
                                    </motion.form>
                                )}
                            </AnimatePresence>
                        </div>
                    </motion.div>
                </>
            )}
        </AnimatePresence>
    );
}
